import {
  targetSignature,
  type ExperimentAssignment,
  type ExperimentManifest,
  type ExperimentTarget,
} from './schema';

function targetHasScope<Scope extends ExperimentTarget['scope']>(
  target: ExperimentTarget,
  scope: Scope,
): target is Extract<ExperimentTarget, { scope: Scope }> {
  return target.scope === scope;
}

/**
 * Whether any of the manifest's variants carries a target of the given scope
 * (optionally further matched on its anchor fields). The serving-point
 * pre-filter: a call site asks "does this experiment touch the thing I am
 * about to serve?" before resolving an assignment for it.
 */
export function manifestTargetsScope<Scope extends ExperimentTarget['scope']>(
  manifest: ExperimentManifest,
  scope: Scope,
  matches?: (target: Extract<ExperimentTarget, { scope: Scope }>) => boolean,
): boolean {
  return manifest.variants.some((variant) =>
    variant.targets.some((target) => targetHasScope(target, scope) && (matches?.(target) ?? true)),
  );
}

/**
 * The manifests whose variants target the given scope — `manifestTargetsScope`
 * lifted over a manifest list for call sites that scan the registry.
 */
export function manifestsTargetingScope<Scope extends ExperimentTarget['scope']>(
  manifests: readonly ExperimentManifest[],
  scope: Scope,
  matches?: (target: Extract<ExperimentTarget, { scope: Scope }>) => boolean,
): ExperimentManifest[] {
  return manifests.filter((manifest) => manifestTargetsScope(manifest, scope, matches));
}

/**
 * The first target of the given scope on a targets carrier (an assignment or a
 * variant), narrowed to that scope's anchor shape. `undefined` when the
 * carrier has no target for the caller's surface — the caller decides whether
 * that is a fallback (voice) or a skip (a surface the experiment doesn't
 * touch).
 */
export function findTargetForScope<Scope extends ExperimentTarget['scope']>(
  carrier: { targets: readonly ExperimentTarget[] },
  scope: Scope,
): Extract<ExperimentTarget, { scope: Scope }> | undefined {
  return carrier.targets.find((candidate) => targetHasScope(candidate, scope));
}

export interface ExperimentTargetArtifact<TArtifact> {
  target: ExperimentTarget;
  artifact: TArtifact;
}

export interface ExperimentTargetSelection<TArtifact> {
  assignment: ExperimentAssignment;
  selected: Array<ExperimentTargetArtifact<TArtifact>>;
  fallback: boolean;
  diagnostics: string[];
}

export function selectExperimentTargets<TArtifact>(
  assignment: ExperimentAssignment,
  registry: readonly ExperimentTargetArtifact<TArtifact>[],
  controlTargets: readonly ExperimentTargetArtifact<TArtifact>[],
): ExperimentTargetSelection<TArtifact> {
  const bySignature = new Map(registry.map((entry) => [targetSignature(entry.target), entry]));
  const selected: Array<ExperimentTargetArtifact<TArtifact>> = [];
  const diagnostics: string[] = [];

  for (const target of assignment.targets) {
    const artifact = bySignature.get(targetSignature(target));
    if (artifact) {
      selected.push(artifact);
    } else {
      diagnostics.push(`missing artifact for ${target.scope}`);
    }
  }

  if (diagnostics.length > 0) {
    return {
      assignment,
      selected: [...controlTargets],
      fallback: true,
      diagnostics,
    };
  }

  return {
    assignment,
    selected,
    fallback: false,
    diagnostics,
  };
}

export function findControlVariantTargets<TArtifact>(
  registry: readonly ExperimentTargetArtifact<TArtifact>[],
  controlTargets: readonly ExperimentTarget[],
): Array<ExperimentTargetArtifact<TArtifact>> {
  const bySignature = new Map(registry.map((entry) => [targetSignature(entry.target), entry]));
  return controlTargets.flatMap((target) => {
    const artifact = bySignature.get(targetSignature(target));
    return artifact ? [artifact] : [];
  });
}
